import PaypalLogo from '@tradera/blueprint/theme/logos/paypal.svg?next-image';
import DpdLogo from '@tradera/blueprint/theme/logos/dpd.svg?next-image';
import MastercardLogo from '@tradera/blueprint/theme/logos/mastercard_vertical.svg?next-image';
import VisaLogo from '@tradera/blueprint/theme/logos/visa.svg?next-image';
import GooglePayLogo from '@tradera/blueprint/theme/logos/google-pay.svg?next-image';
import ApplePayLogo from '@tradera/blueprint/theme/logos/apple-pay.svg?next-image';
import KlarnaLogo from '@tradera/blueprint/theme/logos/klarna.svg?next-image';
import SwishLogo from '@tradera/blueprint/theme/logos/swish_horizontal.svg?next-image';
import DhlLogo from './dhl.svg?next-image';
import TrustlyLogo from '@tradera/blueprint/theme/logos/trustly.svg?next-image';
import styles from './footer-partners.module.scss';
import { useTranslator } from 'tradera/lang/translate';
import { useSelector } from 'react-redux';
import { classNames } from 'tradera/utils/class-names';
import { selectShippingRegionCountryCodeIso2 } from 'tradera/state/shipping-region/selectors';
import { areCountryCodesIso2Equal } from 'tradera/localization/countrycode';

type PartnerLogo = {
    src: string;
    alt: string;
    height: number;
    containerCss?: string;
    imgCss?: string;
    excludedCountries: string[];
    includedCountries: string[];
};

export const FooterPartners = () => {
    const { t } = useTranslator();
    const shippingRegionCountryCodeIso2 = useSelector(
        selectShippingRegionCountryCodeIso2
    );

    const partners: Array<PartnerLogo> = [
        {
            src: '/static/images/shipping/postnord.svg',
            alt: t('footer_partner_shipping', { shippingProvider: 'PostNord' }),
            height: 9,
            containerCss: styles.imageContainer,
            excludedCountries: ['NL'],
            includedCountries: []
        },
        {
            src: '/static/images/shipping/schenker.svg',
            alt: t('footer_partner_shipping', { shippingProvider: 'Schenker' }),
            height: 9,
            containerCss: styles.imageContainer,
            excludedCountries: ['DK', 'NL'],
            includedCountries: []
        },
        {
            src: DhlLogo,
            alt: t('footer_partner_shipping', { shippingProvider: 'DHL' }),
            height: 9,
            containerCss: styles.imageContainer,
            excludedCountries: ['DK'],
            includedCountries: []
        },
        {
            src: '/static/images/shipping/instabox_inverted.svg',
            alt: t('footer_partner_shipping', { shippingProvider: 'Instabox' }),
            height: 9,
            containerCss: styles.imageContainer,
            excludedCountries: [],
            includedCountries: ['SE']
        },
        {
            src: '/static/images/shipping/bring.svg',
            alt: t('footer_partner_shipping', { shippingProvider: 'Bring' }),
            height: 15,
            containerCss: styles.imageContainer,
            excludedCountries: [],
            includedCountries: ['DK']
        },
        {
            src: '/static/images/shipping/gls.svg',
            alt: t('footer_partner_shipping', { shippingProvider: 'GLS' }),
            height: 15,
            containerCss: styles.imageContainer,
            includedCountries: [],
            excludedCountries: ['SE']
        },
        {
            src: DpdLogo,
            alt: t('footer_partner_shipping', { shippingProvider: 'DPD' }),
            height: 15,
            containerCss: styles.imageContainer,
            includedCountries: ['NL'],
            excludedCountries: []
        },
        {
            src: VisaLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'Visa' }),
            height: 10,
            containerCss: styles.imageContainer,
            includedCountries: [],
            excludedCountries: []
        },
        {
            src: MastercardLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'Mastercard' }),
            height: 15,
            containerCss: styles.imageContainer,
            includedCountries: [],
            excludedCountries: []
        },
        {
            src: SwishLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'Swish' }),
            height: 15,
            containerCss: styles.imageContainer,
            includedCountries: ['SE'],
            excludedCountries: []
        },
        {
            src: KlarnaLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'Klarna' }),
            height: 15,
            containerCss: styles.imageContainer,
            includedCountries: ['SE'],
            excludedCountries: []
        },
        {
            src: PaypalLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'PayPal' }),
            height: 12,
            containerCss: styles.imageContainer,
            excludedCountries: ['SE'],
            includedCountries: []
        },
        {
            src: TrustlyLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'Trustly' }),
            height: 11,
            containerCss: styles.imageContainer,
            imgCss: styles.trustlyMargin,
            excludedCountries: ['DK', 'NL'],
            includedCountries: []
        },
        {
            src: GooglePayLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'Google Pay' }),
            height: 21,
            includedCountries: [],
            excludedCountries: []
        },
        {
            src: ApplePayLogo,
            alt: t('footer_partner_payment', { paymentMethod: 'Apple Pay' }),
            height: 21,
            includedCountries: [],
            excludedCountries: []
        }
    ];

    const partnersToRender = partners.filter(
        (partner) =>
            (partner.includedCountries.length === 0 ||
                partner.includedCountries.find((country) =>
                    areCountryCodesIso2Equal(
                        shippingRegionCountryCodeIso2,
                        country
                    )
                )) &&
            !(
                partner.excludedCountries.length > 0 &&
                partner.excludedCountries.find((country) =>
                    areCountryCodesIso2Equal(
                        shippingRegionCountryCodeIso2,
                        country
                    )
                )
            )
    );

    return (
        <>
            {partnersToRender.map((partner: PartnerLogo, index: number) => (
                <span
                    key={`partner_logo_${index}`}
                    className={classNames(
                        partner.containerCss && `${partner.containerCss} px-1`,
                        'mr-1 mb-2'
                    )}>
                    <img
                        src={partner.src}
                        alt={partner.alt}
                        loading="lazy"
                        height={partner.height}
                        className={classNames(partner.imgCss)}
                    />
                </span>
            ))}
        </>
    );
};
